import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Shield, ShieldOff, Trash2, Star, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// Modal admin pra gestão de membros de banda. Consumido por Users.tsx
// quando profileType='band'. Endpoints em /admin/bands/:bandUserId/members.

interface Props {
  bandUserId: string;
  bandLabel: string;
  onClose: () => void;
}

interface Member {
  membership_id: string;
  member_user_id: string;
  role: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  is_admin: boolean;
  is_primary: boolean;
  joined_at: string | null;
  left_at: string | null;
  created_at: string;
  updated_at: string;
  username: string;
  is_active: boolean;
  is_suspended: boolean;
  display_name: string | null;
  avatar_url: string | null;
}

const STATUS_LABEL: Record<Member['status'], string> = {
  pending:  'pendente',
  accepted: 'aceito',
  rejected: 'rejeitado',
};

const STATUS_STYLES: Record<Member['status'], string> = {
  pending:  'bg-amber-950 text-amber-300',
  accepted: 'bg-emerald-950 text-emerald-300',
  rejected: 'bg-zinc-800 text-zinc-400',
};

export function BandMembersModal({ bandUserId, bandLabel, onClose }: Props) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-band-members', bandUserId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/bands/${bandUserId}/members`);
      return data?.data as { band: any; members: Member[] };
    },
  });

  const patchMember = useMutation({
    mutationFn: (args: { memberUserId: string; body: Partial<Pick<Member, 'is_admin' | 'is_primary' | 'role' | 'status'>> }) =>
      api.patch(`/admin/bands/${bandUserId}/members/${args.memberUserId}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-band-members', bandUserId] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || 'Erro ao atualizar membro.'),
  });

  const deleteMember = useMutation({
    mutationFn: (memberUserId: string) =>
      api.delete(`/admin/bands/${bandUserId}/members/${memberUserId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-band-members', bandUserId] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || 'Erro ao remover membro.'),
  });

  const members = data?.members ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-bg-elevated shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-xs uppercase text-text-muted">Banda</div>
            <div className="text-lg font-semibold text-text-primary">{bandLabel}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-bg-input hover:text-text-primary"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-5">
          {isLoading && (
            <div className="flex items-center gap-2 text-text-muted">
              <Loader2 size={16} className="animate-spin" />
              Carregando membros…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-300">Erro ao carregar membros.</div>
          )}
          {!isLoading && members.length === 0 && (
            <div className="text-sm text-text-muted">Essa banda não tem membros cadastrados.</div>
          )}

          <ul className="space-y-2">
            {members.map((m) => (
              <li
                key={m.membership_id}
                className="flex items-center gap-3 rounded-lg border border-border bg-bg-primary p-3"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">
                      {m.display_name || m.username}
                    </span>
                    {m.is_admin && (
                      <span className="inline-flex items-center gap-1 rounded bg-brand-bg px-1.5 py-0.5 text-[10px] font-medium uppercase text-brand">
                        <Shield size={10} />
                        Admin
                      </span>
                    )}
                    {m.is_primary && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-300" title="Banda principal desse membro">
                        <Star size={10} />
                        Principal
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
                    <span>@{m.username}</span>
                    {m.role && <span>· {m.role}</span>}
                    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${STATUS_STYLES[m.status]}`}>
                      {STATUS_LABEL[m.status]}
                    </span>
                    {m.is_suspended && (
                      <span className="rounded bg-red-950 px-1.5 py-0.5 text-[10px] uppercase text-red-300">
                        suspenso
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    title={m.is_admin ? 'Remover admin' : 'Tornar admin'}
                    disabled={patchMember.isPending}
                    onClick={() => patchMember.mutate({ memberUserId: m.member_user_id, body: { is_admin: !m.is_admin } })}
                  >
                    {m.is_admin ? <ShieldOff size={14} /> : <Shield size={14} />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Remover membership"
                    disabled={deleteMember.isPending}
                    onClick={() => {
                      if (confirm(`Remover @${m.username} da banda ${bandLabel}?`)) {
                        deleteMember.mutate(m.member_user_id);
                      }
                    }}
                  >
                    <Trash2 size={14} className="text-red-400" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-border bg-bg-primary px-5 py-3 text-xs text-text-muted">
          Ações não notificam os membros. Pra convite formal com aceite, use o app.
        </div>
      </div>
    </div>
  );
}
